import {
  SYNC_MODES,
  getSyncMode,
  broadcastPlay,
  broadcastPause,
  broadcastSeek,
} from "./MultiFramePlaybackBus";

const DEFAULT_FPS = 7;

// Wall-clock rAF playback. Frames are advanced by elapsed time, NOT by
// counting tick calls — so under CPU pressure we skip frames instead of
// queueing them. This matches MedDream's documented behaviour and is what
// users expect from a cine viewer.
export class MultiFramePlaybackEngine {
  constructor({
    viewportId,
    totalFrames,
    fps = DEFAULT_FPS,
    initialFrame = 0,
    onFrame,
    getNow = () => performance.now(),
    beforePlay,
    afterPause,
  }) {
    this.viewportId = viewportId;
    this.totalFrames = Math.max(1, totalFrames | 0);
    this.fps = fps;
    this.currentFrame = Math.min(this.totalFrames - 1, Math.max(0, initialFrame | 0));
    this.isPlaying = false;
    this.playStart = 0;
    this.playStartFrame = 0;
    this.rafId = null;
    this.renderedFrame = -1;
    this.onFrame = onFrame;
    this.getNow = getNow;
    this._listeners = new Set();
    // Dev-only: ticks-per-second counter for the FPS overlay.
    this._fpsCounter = { lastSecond: 0, count: 0, value: 0 };
    // Set to true when reacting to a remote sync event so we don't echo the
    // local action back onto the bus and create a feedback loop.
    this._suppressBroadcast = false;
    // Optional Promise that resolves when enough frames have been prefetched
    // to make first-loop cine smooth. The first play() blocks the rAF tick
    // until it resolves; later plays bypass it.
    this._warmup = null;
    this._warmupReady = false;
    this._warmupPending = false;
    // Optional async hooks for mixed-series two-axis orchestration:
    //   beforePlay()  runs before each play() — return false (or rejected
    //                 promise) to cancel the play (e.g. active instance is
    //                 single-frame). Returns true / undefined to proceed.
    //   afterPause()  runs after each pause() — typically restores the
    //                 viewport's outer stack so scroll wheel resumes
    //                 navigating between instances.
    this._beforePlay = beforePlay || null;
    this._afterPause = afterPause || null;
    // Frame load tracking — driven by external prefetch via
    // notifyFrameLoaded(). The initial frame is already loaded by
    // setStack so we seed the set with it. `loadedFrameCount` and the
    // derived `isFullyLoaded` flag are exposed in getSnapshot() so the
    // toolbar can show a buffering indicator while frames are still
    // being fetched.
    this._loadedFrames = new Set([this.currentFrame]);
    // Optional sliding-window prefetch callback.  Set via
    // setSlidingWindowCallback().  Receives the 0-indexed current frame
    // and is called on every frame advance during playback.
    this._slidingWindow = null;
  }

  /**
   * Called by the prefetch layer when a frame's image is in the cache.
   * Idempotent — notifying the same index twice is a no-op. Emits so
   * snapshot listeners (the toolbar) see the buffering state clear.
   */
  notifyFrameLoaded(frameIndex) {
    if (
      typeof frameIndex !== "number" ||
      frameIndex < 0 ||
      frameIndex >= this.totalFrames
    ) {
      return;
    }
    if (this._loadedFrames.has(frameIndex)) return;
    this._loadedFrames.add(frameIndex);
    this._emit();
  }

  /**
   * Replace the loaded-frame set with the set of frame indices that are
   * ACTUALLY in the image cache right now (computed by the prefetch layer,
   * which owns the frame ids). Unlike notifyFrameLoaded this is authoritative:
   * it both adds newly-cached frames AND drops evicted ones, so the buffer bar
   * reflects the real cache (grows as frames cache, shrinks as they evict)
   * instead of a monotonic "ever loaded" count. Emits only when the count or
   * the current-frame status actually changed, to avoid needless re-renders.
   */
  syncLoadedFrames(loadedSet) {
    if (!(loadedSet instanceof Set)) return;
    const prevSize = this._loadedFrames ? this._loadedFrames.size : 0;
    const prevHasCurrent = this._loadedFrames
      ? this._loadedFrames.has(this.currentFrame)
      : false;
    this._loadedFrames = loadedSet;
    if (
      loadedSet.size !== prevSize ||
      loadedSet.has(this.currentFrame) !== prevHasCurrent
    ) {
      this._emit();
    }
  }

  /**
   * Hand the engine a promise that resolves when enough frames are cached
   * for cine to play smoothly. The engine's first play() will defer the
   * rAF tick until that promise settles. Idempotent — passing null clears.
   */
  /**
   * Registers a callback that is fired on every frame advance during playback.
   * The callback receives the 0-indexed current frame number.
   * Used by the prefetch layer to slide the high-priority window ahead of
   * the playback cursor.  Pass null to clear.
   */
  setSlidingWindowCallback(callback) {
    this._slidingWindow = typeof callback === "function" ? callback : null;
  }

  setWarmupPromise(promise) {
    if (!promise) {
      this._warmup = null;
      this._warmupReady = true;
      return;
    }
    this._warmup = promise;
    this._warmupReady = false;
    this._warmupPending = false;
  }

  subscribe(listener) {
    this._listeners.add(listener);
    listener(this.getSnapshot());
    return () => this._listeners.delete(listener);
  }

  getSnapshot() {
    const loadedFrameCount = this._loadedFrames ? this._loadedFrames.size : 0;
    return {
      viewportId: this.viewportId,
      currentFrame: this.currentFrame,
      totalFrames: this.totalFrames,
      fps: this.fps,
      isPlaying: this.isPlaying,
      actualFps: this._fpsCounter.value,
      // True while a warmup prefetch is in flight after play() was called.
      // Listeners can use this to show a "buffering" indicator before the
      // first frame actually renders.
      isBuffering: this.isPlaying && !!this._warmup && !this._warmupReady,
      // Frame-level load progress for the toolbar's buffering indicator.
      // `isFullyLoaded` flips true once every frame has been prefetched
      // (success or failure — either way, demand-load won't surprise the
      // user mid-cine).
      loadedFrameCount,
      isFullyLoaded: loadedFrameCount >= this.totalFrames,
      // True when the frame currently on screen has been prefetched.
      // False means the user seeked ahead of the prefetch window — the
      // toolbar will show a viewport loading overlay until it renders.
      isCurrentFrameLoaded: this._loadedFrames
        ? this._loadedFrames.has(this.currentFrame)
        : true,
    };
  }

  _emit() {
    const snapshot = this.getSnapshot();
    this._listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch (err) {
        console.debug("[MultiFrame] listener threw:", err);
      }
    });
  }

  play() {
    if (this.isPlaying || this.totalFrames < 2) return;
    this.isPlaying = true;
    this._emit();
    if (!this._suppressBroadcast && getSyncMode() !== SYNC_MODES.UNSYNCHRONIZED) {
      broadcastPlay(this.viewportId, this.fps, this.currentFrame);
    }

    const startTick = () => {
      if (!this.isPlaying) return;
      this.playStart = this.getNow();
      this.playStartFrame = this.currentFrame;
      this.rafId = requestAnimationFrame(this._tick);
    };

    const afterHook = () => {
      // Defer the first rAF tick until enough frames are cached.
      // Subsequent plays bypass the wait (warmup is sticky once resolved).
      if (this._warmup && !this._warmupReady && !this._warmupPending) {
        this._warmupPending = true;
        this._warmup.finally(() => {
          this._warmupReady = true;
          this._warmupPending = false;
          startTick();
        });
        return;
      }
      startTick();
    };

    // Mixed-series orchestration hook — swap viewport stack to the current
    // instance's frame array before the rAF tick starts. If beforePlay
    // returns false / throws, abort play() entirely (the user has clicked
    // play on a single-frame instance, nothing to cine).
    if (this._beforePlay) {
      Promise.resolve()
        .then(() => this._beforePlay())
        .then((proceed) => {
          if (proceed === false) {
            this.isPlaying = false;
            this._emit();
            return;
          }
          afterHook();
        })
        .catch((err) => {
          console.debug("[MultiFrame] beforePlay hook failed:", err);
          this.isPlaying = false;
          this._emit();
        });
      return;
    }
    afterHook();
  }

  /**
   * Stop playback. By default also fires the afterPause hook (used by
   * mixed-series orchestration to restore the outer stack). Callers
   * that want to do their own stack swap can pass
   * `{ skipAfterPause: true }` to avoid a redundant setStack call.
   */
  pause(opts) {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this._emit();
    if (!this._suppressBroadcast && getSyncMode() !== SYNC_MODES.UNSYNCHRONIZED) {
      broadcastPause(this.viewportId);
    }
    if (opts && opts.skipAfterPause) return;
    // Mixed-series orchestration hook — restore the viewport's outer
    // stack so scroll wheel resumes navigating between instances. Errors
    // are swallowed; pause itself has already taken effect.
    if (this._afterPause) {
      Promise.resolve()
        .then(() => this._afterPause())
        .catch((err) => {
          console.debug("[MultiFrame] afterPause hook failed:", err);
        });
    }
  }

  togglePlay() {
    if (this.isPlaying) this.pause();
    else this.play();
  }

  seekTo(frameIndex) {
    const clamped = Math.max(
      0,
      Math.min(this.totalFrames - 1, Math.floor(frameIndex))
    );
    if (this.isPlaying) {
      // Rebase the clock so subsequent ticks pick up from the new position
      // without snapping back.
      this.playStart = this.getNow();
      this.playStartFrame = clamped;
    }
    this.currentFrame = clamped;
    this._render();
    this._emit();
  }

  step(delta) {
    // Per spec: stepping pauses playback.
    if (this.isPlaying) this.pause();
    this.seekTo(this.currentFrame + delta);
  }

  setFps(fps) {
    if (!Number.isFinite(fps) || fps <= 0) return;
    if (this.isPlaying) {
      // Rebase the clock so the change is seamless — no jump, no restart.
      this.playStart = this.getNow();
      this.playStartFrame = this.currentFrame;
    }
    this.fps = fps;
    this._emit();
  }

  dispose() {
    this.pause();
    this._listeners.clear();
    this.onFrame = null;
  }

  _tick = () => {
    if (!this.isPlaying) return;
    const now = this.getNow();
    const elapsedSec = (now - this.playStart) / 1000;
    const advance = Math.floor(elapsedSec * this.fps);
    const target =
      ((this.playStartFrame + advance) % this.totalFrames + this.totalFrames) %
      this.totalFrames;
    if (target !== this.renderedFrame) {
      this.currentFrame = target;
      this._render();
      this._tickFpsCounter(now);
      this._emit();
      // Slide the prefetch window so frames ahead of the cursor stay warm.
      if (this._slidingWindow) {
        try { this._slidingWindow(target); } catch (e) { /* non-fatal */ }
      }
    }
    this.rafId = requestAnimationFrame(this._tick);
  };

  _render() {
    if (this.renderedFrame === this.currentFrame) return;
    const targetFrame = this.currentFrame;
    if (this.onFrame) {
      try {
        this.onFrame(targetFrame);
        this.renderedFrame = targetFrame;
      } catch (err) {
        // Corrupted-frame skip-and-continue policy (default).
        // The current viewport keeps showing the previous valid frame; the
        // playback clock keeps ticking, so the next attempt is for the
        // following frame index. We log once per corrupted frame so the
        // operator can see what's happening without flooding the console.
        if (!this._loggedCorruptedFrames) {
          this._loggedCorruptedFrames = new Set();
        }
        if (!this._loggedCorruptedFrames.has(targetFrame)) {
          this._loggedCorruptedFrames.add(targetFrame);
          console.debug(
            `[MultiFrame] frame ${targetFrame} could not be rendered; ` +
              `skipping and continuing.`,
            err
          );
        }
        // Mark as rendered so we don't retry on every rAF tick while paused
        // on this frame.
        this.renderedFrame = targetFrame;
      }
    } else {
      this.renderedFrame = targetFrame;
    }
  }

  _tickFpsCounter(now) {
    const sec = Math.floor(now / 1000);
    if (sec !== this._fpsCounter.lastSecond) {
      this._fpsCounter.value = this._fpsCounter.count;
      this._fpsCounter.count = 0;
      this._fpsCounter.lastSecond = sec;
    }
    this._fpsCounter.count += 1;
  }
}

// ---- Engine registry ------------------------------------------------------
// Each viewport that's displaying a multi-frame instance owns at most one
// engine. The registry is keyed by viewport id (the same id Cornerstone uses
// for RenderingEngine.getViewport).

const engines = new Map();
const registryListeners = new Set();

const notifyRegistry = (viewportId, engine) => {
  registryListeners.forEach((fn) => {
    try {
      fn(viewportId, engine);
    } catch (err) {
      console.debug("[MultiFrame] registry listener threw:", err);
    }
  });
};

export const registerEngine = (viewportId, engine) => {
  const existing = engines.get(viewportId);
  if (existing && existing !== engine) {
    existing.dispose();
  }
  engines.set(viewportId, engine);
  notifyRegistry(viewportId, engine);
};

export const getEngine = (viewportId) => engines.get(viewportId);

export const disposeEngine = (viewportId) => {
  const existing = engines.get(viewportId);
  if (existing) {
    existing.dispose();
    engines.delete(viewportId);
    notifyRegistry(viewportId, null);
  }
};

export const disposeAllEngines = () => {
  const ids = Array.from(engines.keys());
  for (const engine of engines.values()) {
    engine.dispose();
  }
  engines.clear();
  ids.forEach((id) => notifyRegistry(id, null));
};

export const listEngines = () => Array.from(engines.entries());

// Subscribe to register/dispose events. The listener is called with
// (viewportId, engine) where `engine` is null on dispose. Returns an
// unsubscribe function.
export const subscribeEngineRegistry = (listener) => {
  registryListeners.add(listener);
  return () => registryListeners.delete(listener);
};
